/**
 * Centralized Logging Utility
 *
 * This module provides environment-aware logging using loglevel library.
 * Log levels are automatically controlled based on NODE_ENV and REACT_APP_LOG_LEVEL.
 *
 * Usage:
 *   import logger from './utils/logger';
 *   logger.debug('Debug information');
 *   logger.info('Informational message');
 *   logger.warn('Warning message');
 *   logger.error('Error message');
 *
 * Module-specific loggers:
 *   import { kafkaLogger, authLogger, pipelineLogger } from './utils/logger';
 *   kafkaLogger.info('Kafka-specific log');
 */

import log from 'loglevel';

// ===========================
// Configuration
// ===========================

/**
 * Determine the appropriate log level based on environment
 * Priority: REACT_APP_LOG_LEVEL > NODE_ENV
 */
function getLogLevel() {
  // If explicitly set via environment variable, use that
  const envLogLevel = process.env.REACT_APP_LOG_LEVEL;
  if (envLogLevel) {
    return envLogLevel.toLowerCase();
  }

  // Otherwise, set based on NODE_ENV
  if (process.env.NODE_ENV === 'production') {
    return 'error'; // Only errors in production
  } else if (process.env.NODE_ENV === 'test') {
    return 'warn'; // Warnings and errors in test
  } else {
    return 'debug'; // Everything in development
  }
}

// ===========================
// Default Logger Configuration
// ===========================

const logLevel = getLogLevel();
log.setLevel(logLevel);

// Add custom formatting for better readability
const originalFactory = log.methodFactory;
log.methodFactory = function (methodName, logLevel, loggerName) {
  const rawMethod = originalFactory(methodName, logLevel, loggerName);

  return function (...args) {
    const timestamp = new Date().toISOString();
    const prefix = loggerName ? `[${loggerName.toUpperCase()}]` : '';
    const messages = [`[${timestamp}]`, prefix, ...args].filter(Boolean);
    rawMethod(...messages);
  };
};

// Re-apply the level to activate the custom factory
log.setLevel(log.getLevel());

// ===========================
// Module-Specific Loggers
// ===========================

/**
 * Kafka Service Logger
 * Handles all Kafka-related operations (topics, publishers, consumers, schemas)
 */
export const kafkaLogger = log.getLogger('kafka');
kafkaLogger.setLevel(process.env.REACT_APP_KAFKA_LOG_LEVEL || logLevel);

/**
 * Authentication Logger
 * Handles login, signup, OAuth, MFA operations
 */
export const authLogger = log.getLogger('auth');
authLogger.setLevel(process.env.REACT_APP_AUTH_LOG_LEVEL || logLevel);

/**
 * Pipeline Logger
 * Handles pipeline creation, execution, monitoring
 */
export const pipelineLogger = log.getLogger('pipeline');
pipelineLogger.setLevel(process.env.REACT_APP_PIPELINE_LOG_LEVEL || logLevel);

/**
 * Microservices Logger
 * Handles Camel microservices pipelines
 */
export const microservicesLogger = log.getLogger('microservices');
microservicesLogger.setLevel(process.env.REACT_APP_MICROSERVICES_LOG_LEVEL || logLevel);

/**
 * API Logger
 * Handles API calls, proxy, and HTTP requests
 */
export const apiLogger = log.getLogger('api');
apiLogger.setLevel(process.env.REACT_APP_API_LOG_LEVEL || logLevel);

/**
 * Connection Logger
 * Handles database and API connections
 */
export const connectionLogger = log.getLogger('connection');
connectionLogger.setLevel(process.env.REACT_APP_CONNECTION_LOG_LEVEL || logLevel);

/**
 * WebSocket Logger
 * Handles WebSocket and real-time communication
 */
export const websocketLogger = log.getLogger('websocket');
websocketLogger.setLevel(process.env.REACT_APP_WEBSOCKET_LOG_LEVEL || logLevel);

/**
 * SQL Logger
 * Handles SQL operations and queries
 */
export const sqlLogger = log.getLogger('sql');
sqlLogger.setLevel(process.env.REACT_APP_SQL_LOG_LEVEL || logLevel);

/**
 * File Logger
 * Handles file operations and file pipelines
 */
export const fileLogger = log.getLogger('file');
fileLogger.setLevel(process.env.REACT_APP_FILE_LOG_LEVEL || logLevel);

/**
 * Performance Logger
 * Handles performance monitoring and metrics
 */
export const performanceLogger = log.getLogger('performance');
performanceLogger.setLevel(process.env.REACT_APP_PERFORMANCE_LOG_LEVEL || logLevel);

/**
 * Settings Logger
 * Handles settings and configuration logging
 */
export const settingsLogger = log.getLogger('settings');
settingsLogger.setLevel(process.env.REACT_APP_SETTINGS_LOG_LEVEL || logLevel);

// ===========================
// Helper Functions
// ===========================

/**
 * Get all available loggers
 */
export function getAllLoggers() {
  return {
    default: log,
    kafka: kafkaLogger,
    auth: authLogger,
    pipeline: pipelineLogger,
    microservices: microservicesLogger,
    api: apiLogger,
    connection: connectionLogger,
    websocket: websocketLogger,
    sql: sqlLogger,
    file: fileLogger,
    performance: performanceLogger,
    settings: settingsLogger,
  };
}

/**
 * Set log level for all loggers at once
 * Useful for debugging
 */
export function setGlobalLogLevel(level) {
  const loggers = getAllLoggers();
  Object.values(loggers).forEach(logger => {
    logger.setLevel(level);
  });
}

/**
 * Get current log levels for all loggers
 * Useful for debugging configuration
 */
export function getLogLevels() {
  const loggers = getAllLoggers();
  const levels = {};
  Object.entries(loggers).forEach(([name, logger]) => {
    levels[name] = logger.getLevel();
  });
  return levels;
}

/**
 * Enable debug mode for specific modules
 * Usage: enableDebugMode(['kafka', 'auth'])
 */
export function enableDebugMode(modules = []) {
  const loggers = getAllLoggers();
  modules.forEach(moduleName => {
    if (loggers[moduleName]) {
      loggers[moduleName].setLevel('debug');
    }
  });
}

/**
 * Backward compatibility wrapper for console
 * This allows existing console.log calls to be captured if needed
 */
export function enableConsoleCapture() {
  const originalConsole = {
    log: console.log,
    error: console.error,
    warn: console.warn,
    info: console.info,
    debug: console.debug,
  };

  console.log = (...args) => log.debug(...args);
  console.info = (...args) => log.info(...args);
  console.warn = (...args) => log.warn(...args);
  console.error = (...args) => log.error(...args);
  console.debug = (...args) => log.debug(...args);

  return originalConsole;
}

/**
 * Restore original console methods
 */
export function disableConsoleCapture(originalConsole) {
  if (originalConsole) {
    console.log = originalConsole.log;
    console.error = originalConsole.error;
    console.warn = originalConsole.warn;
    console.info = originalConsole.info;
    console.debug = originalConsole.debug;
  }
}

// ===========================
// Development Helpers
// ===========================

// Log configuration in development mode
if (process.env.NODE_ENV === 'development') {
  log.info('='.repeat(60));
  log.info('Logger Configuration Initialized');
  log.info('='.repeat(60));
  log.info(`Environment: ${process.env.NODE_ENV}`);
  log.info(`Default Log Level: ${logLevel}`);
  log.info('Module Log Levels:', getLogLevels());
  log.info('='.repeat(60));
}

// Export default logger
export default log;
